// @ts-nocheck
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  formatGroupCommission,
  paymentMethodGroupKey,
  paymentMethodGroupLabel,
  paymentMethodGroupRank,
} from '../../../../utils/payment-methods-utils';
import { getCountryFlagUrl } from '../../../../utils';
import Loading from '../../../common/loading';
import Table from '../../../common/table';
import MultiSelect from '../../../common/multi-select';
import AccordionMenu from '../../../common/accordion-menu';
import l from '../../../../lang';
import './styles.scss';
import { countriesByRegions } from '../../../../config';
import ButtonLink from '../../../common/button-link';
import { Routes } from '../../../../config/routes';
import Switch from '../../../common/switch';
import Dialog from '../../../common/dialog';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import { segmentPaymentMethodActivationCountryPayment } from '../../../../utils/analytics';
import { canModifyPaymentMethods } from '../../../../store/payment-methods/roles';
import Tooltip from '@mui/material/Tooltip';
import { ReactComponent as StatusAvailableIcon } from '../../../../assets/icons/status/completed.svg';
import { ReactComponent as StatusUnavailableIcon } from '../../../../assets/icons/status/rejected.svg';

const REGIONS = [
  { id: 'LA', titleKey: 'region.LA' },
  { id: 'AF_ME', titleKey: 'region.AF_ME' },
  { id: 'AS', titleKey: 'region.AS' },
];

const buildGroups = methods => {
  const byCountry = {};
  methods.forEach(method => {
    if (!byCountry[method.country]) byCountry[method.country] = [];
    byCountry[method.country].push(method);
  });

  const rows = [];
  Object.keys(byCountry).forEach(country => {
    const grouped = {};
    byCountry[country].forEach(method => {
      const key = paymentMethodGroupKey(method);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(method);
    });
    Object.keys(grouped)
      .sort((a, b) => paymentMethodGroupRank(a) - paymentMethodGroupRank(b))
      .forEach(key => {
        rows.push({
          id: `${country}-${key}`,
          country,
          label: paymentMethodGroupLabel(key, grouped[key][0]),
          methods: grouped[key],
        });
      });
  });

  return rows;
};

const PaymentMethodsTable = ({
  countriesInRegion,
  methods,
  paymentMethodsSelected,
  canModify,
  addPaymentMethod,
  removePaymentMethod,
}) => {
  const [filteredCountries, setFilteredCountries] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const countryOptions = useMemo(
    () =>
      countriesInRegion.map(code => ({
        id: code,
        name: l(`countryCode.${code}`),
      })),
    [countriesInRegion]
  );

  const rows = useMemo(() => {
    const selected = filteredCountries ? filteredCountries.split(',').filter(Boolean) : null;
    const visibleCountries = countriesInRegion.filter(code => !selected || selected.includes(code));
    const countryOrder = visibleCountries.reduce((acc, code, index) => {
      acc[code] = index;
      return acc;
    }, {});
    return buildGroups(methods.filter(method => visibleCountries.includes(method.country))).sort(
      (a, b) => (countryOrder[a.country] ?? 0) - (countryOrder[b.country] ?? 0)
    );
  }, [countriesInRegion, filteredCountries, methods]);

  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const selectedIds = new Set((paymentMethodsSelected || []).map(method => method.id));

  return (
    <div className="payment-method-list__table-panel">
      <div className="payment-method-list__header">
        <div className="payment-method-list__title">{l('paymentMethods.byCountry')}</div>
        <div className="payment-method-list__filter-select">
          {!!countryOptions.length && (
            <MultiSelect
              name="filter-country"
              size="sm"
              label={l('paymentMethods.filterByCountry')}
              placeholderSearch={l('searchCountries.label')}
              withSearch
              options={countryOptions}
              onChange={countries => {
                setFilteredCountries(countries || '');
                setPage(0);
              }}
            />
          )}
        </div>
      </div>
      <Table
        dense
        cellAlign="left"
        withoutEdgeSpacers
        headers={[
          <div className="payment-method-list__left">{l('paymentMethod.country')}</div>,
          <div className="payment-method-list__left">{l('paymentMethod.type')}</div>,
          <div className="payment-method-list__right">{`${l('paymentMethod.commission')}*`}</div>,
          <div className="payment-method-list__center">{l('paymentMethod.status')}</div>,
          <div className="payment-method-list__center">{l('paymentMethod.action')}</div>,
        ]}
        body={pageRows.map(row => {
          const enabledMethods = row.methods.filter(method => method.enabled);
          const available = enabledMethods.length > 0;
          const allSelected = available && enabledMethods.every(method => selectedIds.has(method.id));
          return [
            <div className="payment-method-list__country">
              <img
                alt=""
                className="payment-method-list__country-flag"
                src={getCountryFlagUrl(row.country)}
              />
              <span>{l(`countryCode.${row.country}`)}</span>
            </div>,
            <div className="payment-method-list__left">{row.label}</div>,
            <div className="payment-method-list__commision">{formatGroupCommission(row.methods)}</div>,
            available ? (
              <div className="payment-method-list__status">
                <Tooltip title={l('paymentMethod.available')} arrow>
                  <span className="payment-method-list__status-icon-wrap">
                    <StatusAvailableIcon
                      className="payment-method-list__status-icon"
                      aria-label={l('paymentMethod.available')}
                    />
                  </span>
                </Tooltip>
              </div>
            ) : (
              <div className="payment-method-list__status">
                <Tooltip title={l('paymentMethod.notAvailable')} arrow>
                  <span className="payment-method-list__status-icon-wrap">
                    <StatusUnavailableIcon
                      className="payment-method-list__status-icon"
                      aria-label={l('paymentMethod.notAvailable')}
                    />
                  </span>
                </Tooltip>
              </div>
            ),
            <div className="payment-method-list__action">
              <Switch
                checked={allSelected}
                disabled={!available || !canModify}
                onChange={() => {
                  const ids = enabledMethods.map(method => method.id);
                  segmentPaymentMethodActivationCountryPayment({ paymentMethodId: ids[0] });
                  if (allSelected) {
                    removePaymentMethod(ids);
                  } else {
                    addPaymentMethod(ids.filter(id => !selectedIds.has(id)));
                  }
                }}
              />
            </div>,
          ];
        })}
        rowsPerPageOptions={[10, 25, 50]}
        rowsLength={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={(event, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={event => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </div>
  );
};

PaymentMethodsTable.propTypes = {
  countriesInRegion: PropTypes.array,
  methods: PropTypes.array,
  paymentMethodsSelected: PropTypes.array,
  canModify: PropTypes.bool,
  addPaymentMethod: PropTypes.func,
  removePaymentMethod: PropTypes.func,
};

const PaymentMethodList = ({
  loading,
  selectedCountries,
  paymentMethodsSelected,
  allPaymentMethods,
  creditCardEnabled,
  addPaymentMethod,
  removePaymentMethod,
}) => {
  const history = useHistory();
  const { auth } = useSelector(state => state);
  const canModify = canModifyPaymentMethods(auth?.roles);
  const [initMsg, setInitMsg] = useState(false);

  const methods = allPaymentMethods?.data || [];
  const coverage = selectedCountries || [];

  const items = REGIONS.map(region => {
    const countriesInRegion = (countriesByRegions[region.id] || []).filter(code => coverage.includes(code));
    const regionMethods = methods.filter(method => countriesInRegion.includes(method.country));
    const disabled = countriesInRegion.length === 0 || regionMethods.length === 0;
    return {
      id: region.id,
      title: l(region.titleKey),
      disabled,
      content: disabled ? null : (
        <PaymentMethodsTable
          countriesInRegion={countriesInRegion}
          methods={regionMethods}
          paymentMethodsSelected={paymentMethodsSelected}
          canModify={canModify}
          addPaymentMethod={addPaymentMethod}
          removePaymentMethod={removePaymentMethod}
        />
      ),
    };
  });

  return (
    <div className="payment-method-list">
      {loading && <Loading fullScreen />}
      {initMsg && (
        <Dialog
          dividers={false}
          title={l('paymentMethodList.message.title')}
          unpaddingFooter
          unpaddingContent
          body={<div className="confirm-body-container">{l('paymentMethodList.message.body')}</div>}
          footer={
            <div className="confirm-footer-container">
              <ButtonCustom
                name="btn-confirm"
                text={l('common.confirm')}
                variant="contained"
                size="full-bottom"
                onClick={() => setInitMsg(false)}
              />
            </div>
          }
        />
      )}
      {!allPaymentMethods.loading && !creditCardEnabled && (
        <div className="payment-method-list__card-msg">
          <span>{Parser(l('paymentMethod.creditCardDisabled'))} </span>
          <ButtonLink
            text={l('paymentMethod.creditCardDisabled.here')}
            onClick={() => history.push(Routes.COMPANY_INFORMATION)}
          />
        </div>
      )}
      {allPaymentMethods.loading && <Loading />}
      {!allPaymentMethods.loading && (
        <div className="payment-method-list__content">
          <AccordionMenu items={items} />
        </div>
      )}
    </div>
  );
};

PaymentMethodList.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  selectedCountries: PropTypes.array,
  paymentMethodsSelected: PropTypes.array,
  allPaymentMethods: PropTypes.object,
  creditCardEnabled: PropTypes.bool,
  addPaymentMethod: PropTypes.func,
  removePaymentMethod: PropTypes.func,
};

export default PaymentMethodList;
